import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import {
  adminGrowthSnapshotDeleteAction,
  adminGrowthSnapshotsListAction
} from "../Action/action";

const PER_PAGE_OPTIONS = [10, 25, 50];

export default function GrowthSnapshots() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 25 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [query, perPage]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminGrowthSnapshotsListAction({
          page,
          per_page: perPage,
          search: query.trim() || undefined
        });
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setError(res?.message || "Failed to load growth snapshots.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setError(e?.response?.data?.message || e?.message || "Failed to load growth snapshots.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, reloadNonce]);

  const totalPages = Math.max(1, Math.ceil(Number(meta.total || 0)));

  const onDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminGrowthSnapshotDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success("Growth snapshot deleted", { toastId: "gs-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Delete failed", {
        toastId: "gs-delete"
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-content content">
      <div className="page-header-row toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              className="form-control"
              style={{ maxWidth: 280 }}
              placeholder="Search by title…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <span className="small-label">Per Page</span>
            <select
              className="form-control"
              style={{ width: "auto", padding: "6px 10px" }}
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Link className="btn btn-primary" to="/growth-snapshots/new">
          Add Growth Snapshot
        </Link>
      </div>

      <div className="card">
        
        {error ? <div className="alert alert-danger">{error}</div> : null}

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Sections</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>No growth snapshots yet.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>
                      <strong>{row.title || "—"}</strong>
                      
                    </td>
                    <td>{row.sections_count ?? 0}</td>
                    <td>
                      <span className={Number(row.status) === 1 ? "badge badge-success" : "badge"}>
                        {Number(row.status) === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{row.created_at ? String(row.created_at).slice(0, 10) : "—"}</td>
                    <td>
                      <div className="actions-cell">
                        <Link className="action-link" to={`/growth-snapshots/${row.id}/sections`}>
                          Sections
                        </Link>
                        <Link className="action-link" to={`/growth-snapshots/${row.id}/edit`}>
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => {
                            setDeleteRow(row);
                            setDeleteOpen(true);
                          }}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination pagination-row">
          <button type="button" className="btn btn-outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span>
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        title="Delete growth snapshot?"
        itemType="growth snapshot"
        itemName={deleteRow?.title || "this item"}
        confirmDisabled={deleting}
        onCancel={() => {
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={onDelete}
      />
    </div>
  );
}
